import { useRef, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../../store/useStore';
import type { Npc, MyRoomState } from '../../../schema/GameState';
import * as Colyseus from 'colyseus.js';
import { RigidBody, RapierRigidBody, CapsuleCollider } from '@react-three/rapier';
import { CharacterPlaceholder } from './CharacterPlaceholder';
import { Text } from '@react-three/drei';
import { getHeight } from '../Terrain';

export const NpcRenderer = ({ room }: { room: Colyseus.Room<MyRoomState> | null }) => {
    if (!room) return null;
    return <NpcManager room={room} />;
};

const NpcManager = ({ room }: { room: Colyseus.Room<MyRoomState> }) => {
    const [npcList, setNpcList] = useState<{ id: string, data: Npc }[]>([]);

    useEffect(() => {
        if (!room?.state?.npcs) return;

        const update = () => {
            const list: { id: string, data: Npc }[] = [];
            room.state.npcs.forEach((npc: Npc, id: string) => {
                list.push({ id, data: npc });
            });
            setNpcList([...list]);
        };

        room.state.npcs.onAdd(update);
        room.state.npcs.onRemove(update);
        update();

    }, [room]);

    return (
        <group>
            {npcList.map(item => (
                <NpcActor key={item.id} npc={item.data} />
            ))}
        </group>
    );
};

const NpcActor = ({ npc }: { npc: Npc }) => {
    const rb = useRef<RapierRigidBody>(null);
    const targetPos = useRef(new THREE.Vector3(npc.x, npc.y, npc.z));
    const [anim, setAnim] = useState('idle');
    const startDialog = useStore(s => s.startDialog);
    const updateQuestProgress = useStore(s => s.updateQuestProgress);

    useFrame(() => {
        if (!rb.current) return;

        // Snapping logic: Use procedural terrain height if available
        // We assume NPCs are ground-based.
        const groundHeight = getHeight(npc.x, npc.z);
        // If groundHeight is valid (not undefined), use it. otherwise use npc.y
        const yPos = groundHeight !== undefined ? groundHeight : npc.y;

        // Lerp Position
        targetPos.current.set(npc.x, yPos, npc.z);
        const cur = rb.current.translation();
        const next = new THREE.Vector3(cur.x, cur.y, cur.z).lerp(targetPos.current, 0.1);

        rb.current.setNextKinematicTranslation(next);

        // Rotation (Kinematic)
        const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), npc.ry);
        rb.current.setNextKinematicRotation(q);

        // Simple Animation logic
        if (targetPos.current.distanceToSquared(new THREE.Vector3(cur.x, cur.y, cur.z)) > 0.001) {
            // setAnim('run'); 
            // Often "Walk" is better for NPCs but 'run' is what we might have in placeholder
            setAnim(npc.animation === 'idle' ? 'idle' : npc.animation); // Or just trust server?
        }
    });

    return (
        <RigidBody
            ref={rb}
            type="kinematicPosition"
            position={[npc.x, npc.y, npc.z]}
            colliders={false}
            userData={{
                type: 'interactable',
                id: npc.id,
                onInteract: () => {
                    console.log("Interact NPC:", npc.name, npc.dialogId);
                    startDialog(npc.name, npc.dialogId);
                    updateQuestProgress('talk', npc.name, 1);
                }
            }}
        >
            <CapsuleCollider
                args={[0.7, 0.4]}
                position={[0, 0.7, 0]}
            />

            <group position={[0, 0, 0]}>
                <CharacterPlaceholder theme="medieval" animation={anim} />

                {/* Name Tag (Troika) */}
                <Text
                    position={[0, 2.3, 0]}
                    fontSize={0.3}
                    color="#fbbf24" // Amber/Gold for Quest Giver?
                    outlineWidth={0.02}
                    outlineColor="black"
                    anchorX="center"
                    anchorY="middle"
                >
                    {npc.name}
                </Text>
            </group>
        </RigidBody>
    );
};
